import { useReceiptStore } from "@elixir/stores";
import { ReceiptStore } from "@elixir/stores/src/receipt-store/types";
import { DISCOUNT_TYPE, RECEIPT_DISCOUNT_TYPES } from "@elixir/types";
import {
  calculateReceiptBreakdown,
  calculateReceiptPaymode,
  getPositiveNumber,
  restrictToTwoDecimals,
} from "@elixir/utils";
import { ReceiptInputFieldUi } from "ui";

export default function AdditionalDiscount() {
  const additionalDiscountError = false;
  const additionalDiscountConfig = useReceiptStore(
    (state) => state.additionalDiscountConfig
  );
  const setAdditionalDiscountConfig = useReceiptStore(
    (state) => state.setAdditionalDiscountConfig
  );

  const services = useReceiptStore((state) => state.services);
  const paymodes = useReceiptStore((state) => state.paymodes);
  const setPaymodes = useReceiptStore((state) => state.setPaymodes);

  const { grandTotal } = calculateReceiptBreakdown({
    services,
    additionalDiscountConfig,
    paymodes,
  });

  const updatePaymodes = (
    additionalDiscountConfig: ReceiptStore["additionalDiscountConfig"]
  ) => {
    const { grandTotal } = calculateReceiptBreakdown({
      services,
      additionalDiscountConfig,
      paymodes,
    });

    const updatedPaymodes = calculateReceiptPaymode({ grandTotal, paymodes });
    setPaymodes(updatedPaymodes);
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    let discountToShowWithType = e.target.value
      .replace(/[^0-9.%]/g, "")
      .replace(/(\..*)\./g, "$1")
      .replace(/^0+/, "");

    const numericPart = discountToShowWithType.replace("%", "");
    const restrictedNumeric = restrictToTwoDecimals(numericPart);
    discountToShowWithType = e.target.value.includes("%")
      ? restrictedNumeric + "%"
      : restrictedNumeric;

    if (e.target.value.includes("%")) {
      if (
        e.target.value.length === 1 ||
        e.target.value.indexOf("%") < e.target.value.length - 1
      )
        return;

      const updatedAdditionalDiscountConfig = {
        ...additionalDiscountConfig,
        discountToShowWithType,
        discountType: RECEIPT_DISCOUNT_TYPES.find(
          (dt) => dt.id === DISCOUNT_TYPE.PERCENTAGE
        )!,
        discount: parseFloat(restrictedNumeric),
      };

      setAdditionalDiscountConfig(updatedAdditionalDiscountConfig);
      updatePaymodes(updatedAdditionalDiscountConfig);
    } else {
      const updatedAdditionalDiscountConfig = {
        ...additionalDiscountConfig,
        discountToShowWithType,
        discountType: RECEIPT_DISCOUNT_TYPES.find(
          (dt) => dt.id === DISCOUNT_TYPE.AMOUNT
        )!,
        discount: parseFloat(restrictedNumeric),
      };

      setAdditionalDiscountConfig(updatedAdditionalDiscountConfig);
      updatePaymodes(updatedAdditionalDiscountConfig);
    }
  };

  const onFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value === "0") {
      e.target.value = "";
    }
  };

  const onBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value === "") {
      setAdditionalDiscountConfig({
        ...additionalDiscountConfig,
        discountToShowWithType: "0",
      });
    }
  };

  const isAdditionalDiscountNotApplicable = !grandTotal || grandTotal < 0;

  const value = isAdditionalDiscountNotApplicable
    ? "Not Applicable"
    : additionalDiscountConfig.discountToShowWithType;

  return (
    <ReceiptInputFieldUi
      isError={additionalDiscountError}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      isDisabled={isAdditionalDiscountNotApplicable}
      value={value}
      label="Additional Discount"
    />
  );
}
